import React, { useCallback, useMemo } from 'react'

import { ButtonType, CtaType, ImageFit } from 'src/apollo/sites'
import useRefSize from 'src/lib/js/hooks/useRefSize'
import Button from 'src/shared/components/common/button'
import { getRxDesignation } from 'src/shared/components/common/form_input/LocationInput'
import { t } from 'src/translations'

import Image from 'shared/components/common/Image'
import AttributionContextProvider from 'shared/components/common/attribution_context/AttributionContext'
import DropDown from 'shared/components/common/dropdown'
import Link from 'shared/components/common/link'
import {
  useOptionalRestaurant,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import useMoreItems, { MoreItemRefs } from 'shared/js/hooks/useMoreItems'

import {
  AdditionalCTAData,
  CTA,
  CTAData,
  getCtaContent
} from 'public/components/default_template/ctas'
import {
  useOptionalThemeColorScheme,
  useThemeColorScheme
} from 'public/components/default_template/meta/useTheme'

import ExpandableSection from './ExpandableSection'

const filterNavCTAs = (
  ctas: CTAData[],
  useLocationNames: boolean,
  ctaProps?: AdditionalCTAData
) =>
  ctas
    .filter((cta) => cta.nav !== false)
    .map((cta) => getCtaContent(cta, useLocationNames, ctaProps))
    .filter(
      (cta) =>
        cta?.link ||
        cta?.submenus ||
        cta?.type === CtaType.ReservationsModal ||
        cta?.type === 'customAction'
    )

// A link that is wrapped in an AttributionContextProvider if it has a utm_campaign.
// If there is no utm_campaign, it will be an unwrapped link.
const AttributedLink = ({
  cta,
  index,
  className,
  style,
  isButton,
  sameTab
}: {
  cta: CTA
  index?: number
  className?: string
  style?: object
  isButton?: boolean
  sameTab?: boolean
}) => {
  const link = (
    <Link
      key={`link-${cta.text}-${index}`}
      href={cta.link}
      onClick={cta.onClick}
      icon={cta.icon}
      className={className}
      style={style}
      role={isButton ? 'button' : undefined}
      sameTab={sameTab}
    >
      {cta.text}
    </Link>
  )
  return cta.utm_campaign ? (
    <AttributionContextProvider
      key={`attributed-link-${cta.text}-${index}`}
      utm_campaign={cta.utm_campaign}
    >
      {link}
    </AttributionContextProvider>
  ) : (
    link
  )
}

const ExpandableCTA = ({
  cta,
  depth,
  style,
  sameTab
}: {
  cta: CTAData
  depth: number
  style?: object
  sameTab?: boolean
}) => (
  <ExpandableSection label={cta.text} className={`navIndent-${depth}`}>
    <>
      {cta.submenus?.map((subCTA, i) => {
        if (!subCTA) return null

        if (subCTA.submenus && subCTA.submenus.length > 0) {
          return (
            <ExpandableCTA
              key={`expandable-sub-cta-${cta.text}-${i}`}
              cta={subCTA as CTAData}
              depth={depth + 1}
              style={style}
              sameTab={sameTab}
            />
          )
        }
        if (subCTA.link) {
          return (
            <AttributedLink
              key={`sub-cta-${cta.text}-${i}`}
              className={`navIndent-${depth + 1}`}
              cta={subCTA as CTA}
              style={style}
              sameTab={sameTab}
            />
          )
        }
        return null
      })}
    </>
  </ExpandableSection>
)

export const NavItem = ({
  cta,
  expandable,
  className,
  color,
  sameTab
}: {
  cta: CTA
  expandable: boolean
  className?: string
  color?: string | null
  sameTab?: boolean
}) => {
  const style = { ...(color && { color }) }
  if (cta.submenus) {
    return expandable ? (
      <DropDown
        testId='more-menu'
        contentRole='menu'
        aria-label={t('nav-items.more-aria-label')}
        label={cta.text}
        className={className}
        style={style}
        leftAligned
      >
        {cta.submenus.map((submenuCta: CTA, i: number) => {
          if (submenuCta.submenus && submenuCta.submenus.length > 0) {
            return (
              <ExpandableCTA
                cta={submenuCta as CTAData}
                depth={0}
                key={`expandable-cta-${submenuCta.text}-${i}`}
                sameTab={sameTab}
              />
            )
          }

          return (
            <AttributedLink
              cta={submenuCta}
              index={i}
              key={`attributed-link-${submenuCta.text}-${i}`}
              sameTab={sameTab}
            />
          )
        })}
      </DropDown>
    ) : (
      <ExpandableCTA depth={0} cta={cta as CTAData} sameTab={sameTab} />
    )
  }
  if (cta.type === CtaType.ReservationsModal) {
    return (
      <Button
        variant={ButtonType.Text}
        className='link'
        onClick={cta.onClick}
        style={style}
      >
        {cta.text}
      </Button>
    )
  }
  if (cta.type === 'customAction') {
    return (
      <Button
        key={`${cta.text}cta`}
        variant={ButtonType.Text}
        className='link'
        onClick={cta.onClick}
        icon={cta.icon}
        style={style}
      >
        {cta.text}
      </Button>
    )
  }
  if (cta.link) {
    // Links under More menu
    return (
      <AttributedLink
        cta={cta}
        className={className}
        style={{ ...style }}
        isButton
        sameTab={sameTab}
      />
    )
  }
  return null
}

type MobileNavItemsProps = {
  ctas?: CTAData[] | null
  ctaProps?: AdditionalCTAData
  closeOverlay: () => void
  color?: string | null
}
export const MobileNavItems = ({
  ctas,
  ctaProps,
  closeOverlay,
  color
}: MobileNavItemsProps) => {
  const context = useOptionalRestaurant()
  const restaurant = context?.restaurant
  const updateLocation = context?.updateLocation
  const getColorFromTheme = useOptionalThemeColorScheme()
  const showLocationNames: boolean = !!restaurant?.config.showLocationNames
  const sameTab = !!restaurant?.content?.navConfig?.openExternalLinksInSameTab
  const onMenuClick = useCallback(
    (locationId?: string) => () => {
      if (locationId) {
        updateLocation && updateLocation(locationId)
      }
      closeOverlay()
    },
    [updateLocation, closeOverlay]
  )

  const onLinkClick = useCallback(
    (ctaOnClick?: () => void) => () => {
      if (ctaOnClick) {
        ctaOnClick()
      }
      closeOverlay()
    },
    [closeOverlay]
  )

  const locationsWithShortUrl = useMemo(
    () => restaurant?.locations?.filter((loc) => loc.shortUrl) || [],
    [restaurant]
  )

  if (!ctas) {
    return null
  }
  const style = getColorFromTheme(
    (theme) => {
      return {
        color:
          theme.colorOverrides?.navigation?.text ??
          theme.colorScheme.text.default
      }
    },
    { ...(color && { color }) }
  )

  return (
    <>
      {filterNavCTAs(ctas, showLocationNames, ctaProps).map(
        (cta: CTA, i: number) => {
          if (cta.type === 'menu') {
            if (restaurant?.locations?.length === 1) {
              return (
                <Link
                  key='cta-menu'
                  className='mobileLink'
                  href={cta.link}
                  onClick={onMenuClick()}
                  style={style}
                  sameTab={sameTab}
                >
                  {cta.text}
                </Link>
              )
            } else {
              return (
                <ExpandableSection key={`cta-menu-${i}`} label={cta.text}>
                  <AttributionContextProvider utm_campaign='menu'>
                    {locationsWithShortUrl.map((location) => (
                      <Link
                        key={`menu-loc-${location.externalId}`}
                        href={`/menu/${location.shortUrl}`}
                        onClick={onMenuClick(location.externalId)}
                        style={style}
                      >
                        <Image
                          className='icon'
                          alt='Location logo'
                          src='icons/location.svg'
                          fit={ImageFit.Contain}
                        />
                        {getRxDesignation(showLocationNames, location)}
                      </Link>
                    ))}
                  </AttributionContextProvider>
                </ExpandableSection>
              )
            }
          } else if (cta.type == 'reservationsModal') {
            return (
              <Button
                key={`cta${i}`}
                variant={ButtonType.Text}
                className='mobileLink'
                onClick={onLinkClick(ctaProps?.reservationOnOpen)}
                style={style}
              >
                {cta.text}
              </Button>
            )
          } else if (cta.link) {
            return (
              <AttributionContextProvider
                key={`cta${i}`}
                utm_campaign={cta.utm_campaign}
              >
                <Link
                  className='mobileLink'
                  href={cta.link}
                  onClick={onLinkClick(cta.onClick)}
                  icon={cta.icon}
                  style={style}
                  sameTab={sameTab}
                >
                  {cta.text}
                </Link>
              </AttributionContextProvider>
            )
          } else if (cta.submenus) {
            return (
              <ExpandableCTA
                key={`cta${i}`}
                cta={cta as CTAData}
                style={style}
                depth={0}
                sameTab={sameTab}
              />
            )
          } else if (cta.type === 'customAction') {
            return (
              <Button
                key={`${cta.text}cta${i}`}
                variant={ButtonType.Text}
                className='mobileLink'
                onClick={onLinkClick(cta.onClick)}
                icon={cta.icon}
                style={style}
              >
                {cta.text}
              </Button>
            )
          } else {
            return null
          }
        }
      )}
    </>
  )
}

export const FixedNavItems = ({
  ctas,
  ctaProps
}: {
  ctas?: CTAData[] | null
  ctaProps?: AdditionalCTAData
}) => {
  const context = useOptionalRestaurant()

  if (!ctas) {
    return null
  }

  let showLocationNames = false
  if (context) {
    showLocationNames = !!context.restaurant?.config.showLocationNames
  }

  const sameTab =
    !!context?.restaurant?.content?.navConfig?.openExternalLinksInSameTab
  const filteredCTAs = filterNavCTAs(ctas, showLocationNames, ctaProps)

  return (
    <>
      {filteredCTAs.map((cta: CTA, i: number) => (
        <NavItem
          key={`cta${i}`}
          cta={cta}
          expandable={true}
          className='link'
          sameTab={sameTab}
        />
      ))}
    </>
  )
}

type CTAMoreItem = { cta: CTA } & MoreItemRefs

type ResponsiveNavItemsProps = {
  ctas?: CTAData[] | null
  ctaProps?: AdditionalCTAData
  parentRef: React.RefObject<HTMLDivElement>
  moreMenuLabel?: string | null
  color?: string | null
}
export const ResponsiveNavItems = ({
  ctas,
  ctaProps,
  parentRef,
  moreMenuLabel,
  color
}: ResponsiveNavItemsProps) => {
  const { restaurant } = useRestaurant()
  const getColorFromTheme = useThemeColorScheme()
  const showLocationNames: boolean = !!restaurant?.config.showLocationNames
  const sameTab = !!restaurant?.content?.navConfig?.openExternalLinksInSameTab

  const filteredCTARefs = useMemo(
    () =>
      filterNavCTAs(ctas || [], showLocationNames, ctaProps).map((cta) => ({
        cta,
        inMoreMenu: cta?.inMoreMenu,
        ref: React.createRef<HTMLLIElement>()
      })),
    [ctas, ctaProps, showLocationNames]
  )

  const textColor = useMemo(() => {
    return getColorFromTheme((theme) => {
      return (
        theme.colorOverrides?.navigation?.text ?? theme.colorScheme.text.default
      )
    }, color)
  }, [color, getColorFromTheme])
  const { width } = useRefSize(parentRef)
  const { viewItems, moreItems, rerenderRef } = useMoreItems(
    filteredCTARefs,
    100,
    10,
    width
  )

  if (!ctas || !width) {
    return null
  }

  const ctaViewItems = viewItems as CTAMoreItem[]
  const ctaMoreItems = moreItems as CTAMoreItem[]

  // the hidden items allow the useMoreItems logic to measure their width
  // for calculating which items should be shown
  return (
    <>
      <ul className='responsiveNav'>
        {ctaViewItems.map(({ cta }, index: number) => (
          <React.Fragment key={`view-cta${index}`}>
            {index === ctaViewItems.length - 1 && ctaMoreItems.length === 0 ? (
              <div className='hide' ref={rerenderRef} />
            ) : null}
            <li className='viewItem'>
              <NavItem
                cta={cta}
                expandable={true}
                className='link'
                color={textColor}
                sameTab={sameTab}
              />
            </li>
          </React.Fragment>
        ))}
        {/* Dropdown for More menu items */}
        {ctaMoreItems.length > 0 && (
          <li className='viewItem'>
            <DropDown
              testId='more-menu'
              contentRole='menu'
              aria-label={t('nav-items.more-aria-label')}
              label={moreMenuLabel || t('nav-items.more')}
              className='link'
              leftAligned={
                restaurant?.content?.navConfig?.navLayout === 'header-layout-4'
              }
            >
              {ctaMoreItems.map(({ cta }, i: number) => (
                <NavItem
                  key={`more-cta${i}`}
                  cta={cta}
                  expandable={false}
                  sameTab={sameTab}
                />
              ))}
            </DropDown>
          </li>
        )}
      </ul>
      <ul className='hiddenNavItems'>
        {filteredCTARefs.map(
          ({ cta, ref }, i: number) =>
            cta && (
              <React.Fragment key={`hidden-more-cta${i}`}>
                <li ref={ref}>
                  <NavItem
                    cta={cta}
                    expandable={true}
                    className='link'
                    sameTab={sameTab}
                  />
                </li>
              </React.Fragment>
            )
        )}
      </ul>
    </>
  )
}

export default FixedNavItems
